// Écran de la lecture assistée des documents (lot L7, reprise d'Oria) : configuration de la collectivité
// (désactivée par défaut ; simulateur local distinct du raccordement réel, qui n'est pas activé dans ce lot),
// demande de lecture d'une pièce visible avec autorisation explicite de transmission, liste des lectures.
import {
  ETATS_LECTURE,
  FINALITES_LECTURE,
  MODES_ASSISTANCE,
  type LectureAssistee,
  type ModeAssistance,
} from '@cc/contrats';
import {
  Bandeau,
  Bouton,
  CaseACocher,
  Champ,
  Lien,
  PuceEtat,
  Selection,
  Tableau,
  ZoneTexte,
  type ColonneTableau,
} from '@cc/ui';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { Fenetre } from '../achats/Fenetre';
import { dateCourte } from '../urbanisme/PageDemandes';
import {
  configurer,
  demanderLecture,
  INVALIDER_ASSISTANCE,
  useConfiguration,
  useDocumentsLisibles,
  useLectures,
} from './api';
import styles from '../achats/achats.module.css';

export const TON_LECTURE: Record<
  LectureAssistee['etat'],
  'signer' | 'attente' | 'retard' | 'ok' | 'neutre'
> = {
  autorisee: 'attente',
  deposee: 'attente',
  en_analyse: 'attente',
  a_relire: 'signer',
  relue: 'ok',
  echec: 'retard',
  annulee: 'neutre',
};

function FormulaireConfiguration({
  mode,
  onFermer,
  reelDisponible,
}: {
  mode: ModeAssistance;
  onFermer: () => void;
  reelDisponible: boolean;
}) {
  const [v, setV] = useState({ mode, destination: '', conservation: '', motif: '' });
  const action = useAction({
    executer: () =>
      configurer({
        mode: v.mode,
        destination: v.destination || undefined,
        conservation_jours: v.conservation ? Number(v.conservation) : undefined,
        motif: v.motif,
      }),
    succes: 'Configuration enregistrée.',
    invalider: INVALIDER_ASSISTANCE,
    apres: onFermer,
  });
  return (
    <Fenetre
      titre="Configurer la lecture assistée"
      description={
        reelDisponible
          ? 'Le mode réel transmet les pièces autorisées à la destination configurée.'
          : "Le serveur n'a ni adresse autorisée ni compte de service : seul le simulateur local est possible."
      }
      libelle="Enregistrer"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Mode"
        options={Object.entries(MODES_ASSISTANCE).map(([valeur, libelle]) => ({ valeur, libelle }))}
        value={v.mode}
        onChange={(e) => setV((x) => ({ ...x, mode: e.target.value as ModeAssistance }))}
      />
      {v.mode === 'reel' ? (
        <Champ
          libelle="Destination autorisée chez Oria"
          value={v.destination}
          onChange={(e) => setV((x) => ({ ...x, destination: e.target.value }))}
          required
        />
      ) : null}
      {v.mode === 'reel' ? (
        <Champ
          libelle="Conservation chez le prestataire (jours)"
          inputMode="numeric"
          value={v.conservation}
          onChange={(e) => setV((x) => ({ ...x, conservation: e.target.value }))}
          required
        />
      ) : null}
      <ZoneTexte
        libelle="Motif"
        value={v.motif}
        onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))}
        required
      />
    </Fenetre>
  );
}

function FormulaireLecture({
  mode,
  destination,
  onFermer,
}: {
  mode: ModeAssistance;
  destination: string | null;
  onFermer: () => void;
}) {
  const navigate = useNavigate();
  const documents = useDocumentsLisibles(true);
  const [v, setV] = useState({ document: '', finalite: 'classement', autorise: false });
  const cible =
    mode === 'reel'
      ? `la destination « ${destination ?? ''} » chez Oria`
      : 'le simulateur local (aucune transmission hors du serveur)';
  const action = useAction({
    executer: () =>
      demanderLecture({
        document_id: v.document,
        finalite: v.finalite as 'classement',
        autorisation_transmission: v.autorise as true,
      }),
    succes: (l) =>
      l.etat === 'echec'
        ? `Lecture en échec (${l.erreur ?? 'erreur'}) : poursuivre l'instruction manuellement ou relancer.`
        : 'Pièce transmise pour lecture.',
    invalider: INVALIDER_ASSISTANCE,
    apres: (l) => {
      onFermer();
      void navigate(`/assistance-documentaire/lectures/${l.id}`);
    },
  });
  const lisibles = (documents.data?.elements ?? []).filter(
    (d) => ['application/pdf', 'image/jpeg', 'image/png'].includes(d.type_mime) && !d.archive_le,
  );
  return (
    <Fenetre
      titre="Demander une lecture assistée"
      description={`La pièce sera transmise à ${cible}. Les propositions reçues sont relues une à une ; elles ne modifient aucun dossier.`}
      libelle="Transmettre"
      enCours={action.enCours}
      onFermer={onFermer}
      onValider={() => action.lancer(undefined)}
      erreur={action.messageErreur}
    >
      <Selection
        libelle="Pièce"
        options={lisibles.map((d) => ({ valeur: d.id, libelle: d.titre }))}
        optionVide="Choisir une pièce visible"
        value={v.document}
        onChange={(e) => setV((x) => ({ ...x, document: e.target.value }))}
        required
      />
      <Selection
        libelle="Finalité"
        options={Object.entries(FINALITES_LECTURE).map(([valeur, libelle]) => ({
          valeur,
          libelle,
        }))}
        value={v.finalite}
        onChange={(e) => setV((x) => ({ ...x, finalite: e.target.value }))}
      />
      <CaseACocher
        libelle={`J'autorise la transmission de cette pièce vers ${cible}, pour la finalité choisie`}
        checked={v.autorise}
        onChange={(e) => setV((x) => ({ ...x, autorise: e.target.checked }))}
      />
    </Fenetre>
  );
}

export function PageAssistance() {
  const { contexte } = useSession();
  const configuration = useConfiguration();
  const lectures = useLectures(peut(contexte, 'document', 'voir'));
  const [dialogue, setDialogue] = useState<'configuration' | 'lecture' | null>(null);
  const conf = configuration.data;
  const colonnes: ColonneTableau<LectureAssistee>[] = [
    {
      id: 'document',
      entete: 'Pièce',
      enTeteLigne: true,
      masquable: false,
      cellule: (l) => (
        <Lien href={`/assistance-documentaire/lectures/${l.id}`}>{l.document.titre}</Lien>
      ),
    },
    {
      id: 'mode',
      entete: 'Mode',
      cellule: (l) => (l.mode === 'reel' ? 'Raccordement réel' : 'Simulateur local'),
    },
    { id: 'finalite', entete: 'Finalité', cellule: (l) => FINALITES_LECTURE[l.finalite] },
    {
      id: 'demande',
      entete: 'Demandée',
      cellule: (l) => `${dateCourte(l.cree_le)}, ${l.demandee_par}`,
      valeurTri: (l) => l.cree_le,
    },
    {
      id: 'etat',
      entete: 'État',
      cellule: (l) => (
        <PuceEtat
          ton={TON_LECTURE[l.etat]}
          libelle={`${ETATS_LECTURE[l.etat]}${l.erreur ? ` (${l.erreur})` : ''}`}
        />
      ),
    },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Documents"
        titre="Lecture assistée des documents"
        description="Proposition de champs lus dans une pièce par Oria, relue et confirmée par un agent. Désactivée par défaut ; aucune pièce n'est transmise sans autorisation explicite et destination configurée."
        actions={
          <>
            {conf && conf.mode !== 'desactive' && peut(contexte, 'document', 'modifier') ? (
              <Bouton variante="principal" onClick={() => setDialogue('lecture')}>
                Demander une lecture
              </Bouton>
            ) : null}
            {peut(contexte, 'parametres', 'administrer') ? (
              <Bouton variante="secondaire" onClick={() => setDialogue('configuration')}>
                Configurer
              </Bouton>
            ) : null}
          </>
        }
      />
      <EtatRequete
        chargement={configuration.isPending}
        erreur={configuration.error}
        onReessayer={() => void configuration.refetch()}
        lot="L7"
        fonction="La configuration de la lecture assistée"
      >
        {conf ? (
          <section className={styles.bloc} aria-labelledby="titre-configuration">
            <h2 id="titre-configuration">Configuration</h2>
            <dl className={styles.fiche}>
              <dt>Mode</dt>
              <dd>{MODES_ASSISTANCE[conf.mode]}</dd>
              <dt>Destination</dt>
              <dd>{conf.destination ?? 'aucune'}</dd>
              <dt>Conservation chez le prestataire</dt>
              <dd>
                {conf.conservation_jours ? `${conf.conservation_jours} jours` : 'non configurée'}
              </dd>
              <dt>Raccordement réel</dt>
              <dd>
                {conf.raccordement_reel_disponible
                  ? 'Possible (adresse et compte de service présents sur le serveur)'
                  : 'Non disponible : ni adresse autorisée ni compte de service sur le serveur'}
              </dd>
            </dl>
            {conf.mode === 'desactive' ? (
              <Bandeau type="info">
                La lecture assistée est désactivée : l&apos;instruction se fait manuellement, comme
                d&apos;habitude.
              </Bandeau>
            ) : null}
            {conf.mode === 'simulateur' ? (
              <Bandeau type="info">
                Simulateur local : aucune pièce ne quitte le serveur ; les propositions ne viennent
                que des octets reçus (empreinte, taille, pages) et ne sont pas une lecture réelle.
              </Bandeau>
            ) : null}
          </section>
        ) : null}
      </EtatRequete>
      <section className={styles.bloc} aria-labelledby="titre-lectures">
        <h2 id="titre-lectures">Lectures</h2>
        {!peut(contexte, 'document', 'voir') ? (
          <p className={styles.detail}>
            La liste des lectures est réservée aux comptes qui voient les documents.
          </p>
        ) : null}
        <EtatRequete
          chargement={lectures.isPending && lectures.fetchStatus !== 'idle'}
          erreur={lectures.error}
          onReessayer={() => void lectures.refetch()}
          lot="L7"
          fonction="La liste des lectures"
        >
          {(lectures.data?.elements.length ?? 0) === 0 ? (
            <p className={styles.detail}>Aucune lecture.</p>
          ) : (
            <Tableau
              legende="Lectures assistées"
              colonnes={colonnes}
              lignes={lectures.data?.elements ?? []}
              cleLigne={(l) => l.id}
              cleColonnes="assistance-lectures"
            />
          )}
        </EtatRequete>
      </section>
      {dialogue === 'configuration' && conf ? (
        <FormulaireConfiguration
          mode={conf.mode}
          reelDisponible={conf.raccordement_reel_disponible}
          onFermer={() => setDialogue(null)}
        />
      ) : null}
      {dialogue === 'lecture' && conf ? (
        <FormulaireLecture
          mode={conf.mode}
          destination={conf.destination}
          onFermer={() => setDialogue(null)}
        />
      ) : null}
    </>
  );
}
